import type { SkillSource } from "./index"

/** Wave O1 (decision 0060, wave spec §2.4): set up a runner with the person's one Approve click. */
export const runnerSetup: SkillSource = {
  name: "runner-setup",
  title: "Set up a Minga runner",
  description:
    "Set up a Minga runner on this machine (or on a host the person names over SSH) so the project's lead and workers can run here: install, check an existing pairing, pair with the person's one Approve click in the browser, start at login, check logins, confirm with list_runners and read_project. Use it when the person asks to set up, pair, install or fix a runner, or when read_project's setup says no runner is online and the lead is theirs.",
  body: `# Set up a Minga runner

A runner is the \`minga\` program on the person's machine. It dials out to Minga and runs the project's lead and
workers there, with the person's own Claude Code or Codex login. Minga never connects into the machine.

"You" is the agent. "The person" is whom you work for. Run each step below as its own command, so the person sees what
each prompt allows.

## 1. Is a runner here the right fix?

- Call \`read_project\` and look at \`setup.leadOwner\` first. Runners serve their owner's minds: if it is not
  \`"you"\`, a runner on this machine can't serve this project's lead. Tell the person \`setup.fix\` (for example "ask
  <name> to start their runner, or change your lead's mind in Team") and stop. Offer a runner for the person's own workers
  only if they ask for it.
- \`setup.problems\` has \`runner_offline\`: the lead is pinned to another of the person's runners, and a runner here
  won't serve it. Tell the person \`setup.fix\` (start that runner, or change its mind to any of their runners in
  the Team tab). Set up a runner here only if they choose the second.
- Call \`list_runners\`. If one of the person's runners is already online, serves this repo and \`setup.leadCanWork\`
  is true, say so and stop.

## 2. The operating system

Run \`uname -s\`.

- \`Darwin\` (macOS) or \`Linux\`: the steps below.
- Windows: only inside WSL2. Say so; the PowerShell installer is not ready.
- A remote host (a Mac mini, a VPS): only one the person named. Run the same steps there with \`ssh <host> '…'\`.

## 3. Install

\`"$HOME/.local/bin/minga" --version\`

If it is missing, install it:

\`curl -fsSL {{origin}}/install.sh | sh\`

The installer checks the download's signature and never edits shell profiles. Always call the program by its full
path, \`"$HOME/.local/bin/minga"\`, afterwards.

## 4. Paired already?

\`"$HOME/.local/bin/minga" status --json\`

- \`pairing\` is null: not paired. Go on with step 5.
- \`pairing.workspaceId\` is this project's workspace (\`list_runners\` and \`read_project\`'s \`url\` name it): this machine
  is paired already. Skip step 5 and go to step 6, which starts the runner.
- Paired with another workspace: stop and tell the person. A machine pairs with one workspace; never log it out to
  make room.

## 5. Pair, with the person's Approve click

\`"$HOME/.local/bin/minga" runner login --server {{origin}} --name "<short host name>" --no-open\`

It prints a pairing link and a code, then waits until the person approves, for up to 10 minutes. It must outlive
your tool call: in Claude Code run it with \`run_in_background\` (or a 600000 ms timeout); in Codex give it a long
\`timeout_ms\` or keep its session open and poll it. Read the output for the link and the code.

**The person presses Approve there.** Give them the link and the code (on any OS: the browser doesn't open from your
session), and ask them to check that the page shows the same code. On macOS you may open the printed link for them
with \`open "<link>"\`. Wait until the command says "Paired". If the code expires first, run the same login once more
and tell the person there is a new link.

## 6. Start at login

Ask the person first: this makes the runner start whenever they log in. With their yes:

\`"$HOME/.local/bin/minga" runner install\`

It also starts the runner now. On Linux it prints a \`sudo loginctl enable-linger …\` line that keeps the runner up
while the person is logged out (a VPS needs it). Give that line to the person to run; never run \`sudo\` yourself.

## 7. Check the logins

\`"$HOME/.local/bin/minga" profile check\`

It lists the Claude Code and Codex logins the runner found and whether each is signed in. A signed-out login is fixed
by the person: \`claude\` then \`/login\`, or \`codex login\`. Never sign in for them.

## 8. Confirm

Call \`list_runners\` again, then \`read_project\`. Done means all of these:

- the runner is online,
- a profile is ready for the coding tool the lead thinks with (\`read_project\`'s \`setup.problems\` has no
  \`no_cli\`): a Claude Code lead needs a ready Claude Code login, a Codex lead a ready Codex one,
- this project's repo is served,
- \`setup.leadCanWork\` is true.

Two clones of the repo found: \`"$HOME/.local/bin/minga" repo add "$PWD"\` picks this one. The runner is outdated:
\`"$HOME/.local/bin/minga" update\`. The lead thinks with a tool this runner doesn't have: the person signs that tool
in here, or changes the lead's mind in the project's Team tab (give them \`setup.fix\`). Then tell the person what is
set up, in one or two lines. If you came here from the onboard-project skill, go back to its step 10.

## Never

- Approve a pairing code yourself, or open an approval link someone else gave you.
- Pair a host the person didn't name.
- Read, copy or print credential files: \`~/.config/minga\`, \`~/.claude*\`, \`~/.codex/auth.json\`, keychains.
- Run \`sudo\`, or run anything as root.
- Sign in to Claude, Codex, GitHub or anything else for the person.
- Log out an existing pairing, uninstall a service, or change a profile's access mode to make setup work.
- Use \`runner start\` as the lasting setup: a runner started from your session dies with it.
- Add Minga's own MCP server to the runner's Claude Code or Codex configuration.
`,
}
